import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dataset } from 'app/data/datasets';
import { DataLink, FOCUS, Panel } from './ui';

/**
 * The copy-paste snippet.
 *
 * We render only the call itself — no example output rows. The registry holds
 * no sample data, and inventing a DataFrame preview would put fabricated values
 * on a page whose entire premise is that we do not guess.
 *
 * `sdkQuery` is now read from the SDK's endpoint registry and confirmed by
 * executing each call, so the snippet is trustworthy where it exists. Where it
 * is `undefined` the dataset genuinely has no sov.data() endpoint, and we say
 * so rather than emitting a call that would raise. The previous version of this
 * file inferred the query from the docs URL path and was wrong for eleven
 * datasets — do not reintroduce that inference.
 */

const buildSnippet = (sdkQuery: string) =>
  `import sovai as sov\n\ndf = sov.data("${sdkQuery}")`;

type CopyState = 'idle' | 'copied' | 'failed';

/**
 * Shown when the registry has no endpoint for this dataset. Deliberately
 * generic: how each such dataset is actually reached is stated in its registry
 * `summary`, so this panel points at the documentation instead of hardcoding a
 * per-dataset call signature in the view layer.
 */
const NoEndpoint: React.FC<{ dataset: Dataset }> = ({ dataset }) => (
  <Panel id="usage-heading" label="Fetch" aside="No endpoint">
    <p className="max-w-3xl text-[13px] leading-relaxed text-gray-300">
      {dataset.name} has no{' '}
      <span className="font-mono text-gray-100">sov.data()</span> endpoint
      registered in the SDK, so there is no snippet to copy. It is reached
      another way — the{' '}
      <DataLink href={dataset.docsUrl}>{dataset.name} documentation</DataLink>{' '}
      describes the supported access path.
    </p>
    <p className="mt-3 max-w-3xl text-[13px] leading-relaxed text-gray-400">
      We show this rather than a plausible-looking call because a snippet that
      raises on paste is worse than no snippet at all.
    </p>
  </Panel>
);

export const CodeSample: React.FC<{ dataset: Dataset }> = ({ dataset }) => {
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const snippet = dataset.sdkQuery ? buildSnippet(dataset.sdkQuery) : '';

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const flash = useCallback((state: CopyState) => {
    setCopyState(state);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopyState('idle'), 2000);
  }, []);

  const handleCopy = useCallback(async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(snippet);
        flash('copied');
        return;
      }
      // Fallback for non-secure contexts, where navigator.clipboard is absent.
      const textarea = document.createElement('textarea');
      textarea.value = snippet;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      flash(ok ? 'copied' : 'failed');
    } catch {
      flash('failed');
    }
  }, [snippet, flash]);

  const label =
    copyState === 'copied'
      ? 'Copied'
      : copyState === 'failed'
      ? 'Copy failed'
      : 'Copy';

  // After the hooks, so hook order stays stable across both branches.
  if (!dataset.sdkQuery) return <NoEndpoint dataset={dataset} />;

  return (
    <Panel id="usage-heading" label="Fetch" aside="Python">
      <div className="border border-gray-800">
        <div className="flex items-center justify-between gap-4 border-b border-gray-800 px-3 py-1.5">
          <span className="font-mono text-[11px] tracking-tight text-gray-400">
            sovai
          </span>
          <button
            type="button"
            onClick={handleCopy}
            className={`border border-gray-700 px-2 py-[3px] font-mono text-[11px] text-gray-300 hover:border-blue-400/60 hover:text-white ${FOCUS}`}
          >
            {label}
            <span className="sr-only"> code sample to clipboard</span>
          </button>
        </div>
        <div className="overflow-x-auto">
          <pre className="px-3 py-3 font-mono text-[13px] leading-relaxed">
            <code className="whitespace-pre text-green-400">{snippet}</code>
          </pre>
        </div>
      </div>
      {/* Announced separately so the button's own label is not re-read on focus. */}
      <p aria-live="polite" className="sr-only">
        {copyState === 'copied'
          ? 'Code sample copied to clipboard'
          : copyState === 'failed'
          ? 'Copy failed'
          : ''}
      </p>

      {/*
        This query string is read from the SDK's endpoint registry and was
        confirmed by executing the call, so it is stated as fact rather than
        hedged. Documentation remains authoritative for arguments and semantics.
      */}
      <p className="mt-3 text-[13px] leading-relaxed text-gray-400">
        <span className="font-mono text-gray-200">{dataset.sdkQuery}</span> is
        the endpoint registered in the sovai SDK, verified by running the call.
        For arguments, field semantics and worked examples, see{' '}
        <DataLink href={dataset.docsUrl}>
          the {dataset.name} documentation
        </DataLink>
        .
      </p>
    </Panel>
  );
};

export default CodeSample;
